import { downloadBundleFiles } from './bundleDownloader'
import type { BundleFileStore } from './bundleFileStore'
import { NIGHT_FRAME_FILES, verifyBundle } from './bundleVerifier'
import { broken } from './broken'
import { deliveryEventParams, MAP_UPDATE_EVENTS } from './deliveryEventParams'
import {
  initialDeliveryState,
  isBadManifest,
  type DeliveryBundle,
  type DeliveryEvent,
  type DeliveryState,
} from './deliveryState'
import type { BundleFiles, DeliveryRecord, DeliveryStore } from './deliveryStore'
import { firstFrame } from './firstFrame'
import { launch } from './launch'
import { mapFileContentType } from './mapFileContentType'
import { offer } from './offer'
import { fetchManifest, type ManifestFile } from './remoteManifest'
import { sha256Hex } from './sha256Hex'
import { staged } from './staged'

/** The map `format` this client reads (`MapSchemaV2.format`). */
export const SUPPORTED_MAP_FORMAT = 2

/** The manifest is checked at most once per this interval (spec §3.4). */
export const MANIFEST_CHECK_INTERVAL_MS = 60 * 60 * 1000

/**
 * A download the page did not get to finish — closed, not failed (a failure
 * counts an attempt and waits out its back-off). It resumes at the next page
 * load instead of an hour later.
 */
function interrupted(record: DeliveryRecord): boolean {
  return (record.downloading ?? null) !== null && (record.retryAttempts ?? 0) === 0
}

/**
 * The wait before the next attempt after `failures` failed downloads in a
 * row: a minute, doubling, capped at the check interval (spec §3.4).
 */
export function retryDelayMs(failures: number): number {
  const doublings = Math.min(Math.max(failures - 1, 0), 30)
  return Math.min(60_000 * 2 ** doublings, MANIFEST_CHECK_INTERVAL_MS)
}

/**
 * Manifest roles the web client uses; `texts` / `texts-night` (label MMTG) are
 * mobile-only. The `*-night` geometry roles are the late-night frame
 * (TASK-1136, New York); a manifest without them is a whole bundle.
 */
export const WEB_FILE_ROLES: ReadonlySet<string> = new Set([
  'schema',
  'connections',
  'stations',
  'additionals',
  'transitions',
  'texts-svg',
  'texts-svg-night',
  'transitions-order',
  'connections-night',
  'stations-night',
  'additionals-night',
  'transitions-night',
  'transitions-order-night',
])

/** The bundled set when its `bundle.json` cannot be read: never newer than a download. */
const FALLBACK_BUNDLED: DeliveryBundle = {
  source: 'bundled',
  sequence: 0,
  format: SUPPORTED_MAP_FORMAT,
  manifest: 'bundled',
  complete: true,
}

export interface MapBundleSessionDeps {
  readonly fetch: typeof fetch
  readonly store: DeliveryStore
  /** `null` where Cache Storage is unavailable — the bundled map only. */
  readonly files: BundleFileStore | null
  readonly report: (event: string, params: Record<string, unknown>) => void
  readonly now: () => number
  readonly createObjectUrl: (blob: Blob) => string
  readonly reload: () => void
  /** Free storage in bytes, `null` when unknown. */
  readonly freeBytes: () => Promise<number | null>
  /** Where the bundled set lives (`/map-v2`) and the API prefix (`/api/v2`). */
  readonly bundledPath: string
  readonly apiPrefix: string
}

/** The bundle a page uses, decided once per page load. */
export interface MapBundleChoice {
  readonly bundle: DeliveryBundle
  /** The downloaded bundle's files; `null` for the bundled set. */
  readonly files: BundleFiles | null
  /** Whether this page ran the launch decision (the map UI) or only read it. */
  readonly launched: boolean
}

/**
 * Runtime map delivery for one page load (spec §3.9: a new bundle is used
 * from the next page load). The map UI calls `begin('map')`, which runs the
 * shared launch decision and persists it before any map file is read; any
 * other page reads the last confirmed choice without changing it
 * (`begin('peek')`), so a page that never draws the map cannot count as a
 * launch crash. Every map file goes through `assetUrl`, so a page never mixes
 * files of two bundles. After the first frame the manifest is checked and a
 * newer bundle is downloaded, verified and staged in the background.
 */
export class MapBundleSession {
  private choice: Promise<MapBundleChoice> | null = null
  private bundledJson: Promise<ArrayBuffer | null> | null = null
  private bundled: DeliveryBundle | null = null
  private confirmed = false
  private updating = false
  private readonly urls = new Map<string, Promise<string>>()
  private readonly resolvedUrls = new Map<string, string>()

  private readonly deps: MapBundleSessionDeps

  constructor(deps: MapBundleSessionDeps) {
    this.deps = deps
  }

  begin(mode: 'map' | 'peek'): Promise<MapBundleChoice> {
    this.choice ??= this.choose(mode)
    return this.choice
  }

  /** The page's choice if one was made, without making it. */
  private async decided(): Promise<MapBundleChoice | null> {
    return this.choice === null ? null : this.choice
  }

  /**
   * Whether this page's bundle has the late-night frame (TASK-1136): every
   * `*-night.mmtg` file. A downloaded bundle names its files; the bundled set
   * lists them in its `bundle.json`. Never throws — no frame when unknown.
   */
  async hasNightFrame(): Promise<boolean> {
    try {
      const choice = await this.begin('peek')
      if (choice.files !== null && this.deps.files !== null) {
        const files = choice.files
        return NIGHT_FRAME_FILES.every((name) => files[name] !== undefined)
      }
      const bytes = await this.readBundledJson()
      if (bytes === null) return false
      const json = JSON.parse(new TextDecoder().decode(bytes)) as { files?: unknown }
      const names = new Set(
        Array.isArray(json.files)
          ? json.files.map((f) => (f as { name?: unknown } | null)?.name)
          : [],
      )
      return NIGHT_FRAME_FILES.every((name) => names.has(name))
    } catch {
      return false
    }
  }

  /** The URL of a map file of this page's bundle (`schema.json`, `stations-dark.mmtg`, …). */
  async assetUrl(name: string): Promise<string> {
    const choice = await this.begin('peek')
    const file = choice.files?.[name]
    if (file === undefined || this.deps.files === null) {
      const bundled = this.bundledUrl(name)
      this.resolvedUrls.set(name, bundled)
      return bundled
    }
    let url = this.urls.get(name)
    if (url === undefined) {
      url = this.objectUrl(file.sha256, file.size, name).then((resolved) => {
        this.resolvedUrls.set(name, resolved)
        return resolved
      })
      this.urls.set(name, url)
    }
    return url
  }

  /**
   * A map file of this page's bundle as a `Response`. Downloaded files are
   * read straight from Cache Storage — never through `fetch(blob:…)`, which
   * the site's CSP (`connect-src`) blocks; bundled ones are fetched.
   */
  async fetchAsset(name: string): Promise<Response> {
    const choice = await this.begin('peek')
    const file = choice.files?.[name]
    const store = this.deps.files
    if (file !== undefined && store !== null) {
      const blob = await store.get(file.sha256, file.size).catch(() => null)
      if (blob !== null) return new Response(blob)
      void this.reportBroken()
    }
    return this.deps.fetch(this.bundledUrl(name))
  }

  /**
   * `assetUrl` when it is already known without waiting — always for a page
   * that can only use the bundled set, else once resolved — or `null`.
   */
  assetUrlIfKnown(name: string): string | null {
    if (this.deps.files === null) return this.bundledUrl(name)
    return this.resolvedUrls.get(name) ?? null
  }

  /** The map drew its first frame: confirm the bundle, clean up, look for an update. */
  async confirmFirstFrame(): Promise<void> {
    const choice = await this.decided()
    if (choice?.launched !== true || this.confirmed) return
    this.confirmed = true
    const record = this.load()
    const r = firstFrame(record.state)
    const files = keptFiles(record.files, r.state)
    this.save({ ...record, state: r.state, files })
    // An unfinished download's files are in the store but in no kept bundle
    // yet; they are swept once it is staged or abandoned, not before.
    if ((record.downloading ?? null) === null) await this.sweep(files)
    await this.checkForUpdate()
  }

  /** A map file of this page's bundle failed to load or parse (spec §3.6 `Broken`). */
  async reportBroken(): Promise<void> {
    const choice = await this.decided()
    if (choice?.launched !== true) return
    const record = this.load()
    const r = broken(record.state)
    this.save({ ...record, state: r.state })
    this.emit(r.events)
    if (choice.bundle.source === 'downloaded') this.deps.reload()
  }

  /**
   * Checks the manifest (at most hourly) and downloads a newer bundle — only
   * after this page launched the map. Never makes the launch decision itself
   * (a check racing the map would pin the page to a read-only peek). Never throws.
   */
  async checkForUpdate(): Promise<void> {
    const files = this.deps.files
    if (files === null || this.updating) return
    const choice = await this.decided()
    if (choice?.launched !== true) return
    this.updating = true
    try {
      await this.update(files)
    } catch {
      // Delivery never breaks the map: the next check retries.
    } finally {
      this.updating = false
    }
  }

  private async update(files: BundleFileStore): Promise<void> {
    let record = this.load()
    const now = this.deps.now()
    if (
      record.checkedAt !== null &&
      now >= record.checkedAt &&
      now - record.checkedAt < MANIFEST_CHECK_INTERVAL_MS &&
      !interrupted(record)
    ) {
      return
    }
    const fetched = await fetchManifest(
      this.deps.fetch,
      `${this.deps.apiPrefix}/manifest`,
      record.etag,
    )
    record = {
      ...record,
      checkedAt: now,
      etag: fetched.kind === 'manifest' ? fetched.etag : record.etag,
    }
    this.save(record)
    if (fetched.kind !== 'manifest') {
      this.deps.report(MAP_UPDATE_EVENTS.checked, {
        status: fetched.kind === 'unchanged' ? 'unchanged' : fetched.reason,
      })
      return
    }
    const manifest = fetched.manifest
    this.deps.report(MAP_UPDATE_EVENTS.checked, { status: 'manifest', sequence: manifest.sequence })
    const remote: DeliveryBundle = {
      source: 'downloaded',
      sequence: manifest.sequence,
      format: manifest.format,
      manifest: manifest.id,
      complete: true,
    }
    if (!offer(record.state, remote, SUPPORTED_MAP_FORMAT)) {
      this.save({ ...record, downloading: null })
      return
    }
    // Until the bundle is staged its ETag is not ours to keep: a page closed
    // mid-download must get the manifest again, not a 304.
    this.save({ ...record, etag: null, downloading: manifest.id })

    const wanted = manifest.files.filter((f) => WEB_FILE_ROLES.has(f.role))
    const missing = await this.missingBytes(wanted, files)
    const free = await this.deps.freeBytes()
    if (free !== null && free < 2 * missing) {
      this.retryLater()
      this.deps.report(MAP_UPDATE_EVENTS.downloadFailed, {
        sequence: manifest.sequence,
        reason: 'insufficient_space',
      })
      return
    }
    this.deps.report(MAP_UPDATE_EVENTS.downloadStarted, {
      sequence: manifest.sequence,
      version: manifest.version,
      bytes: missing,
    })
    const started = this.deps.now()
    const outcome = await downloadBundleFiles(
      wanted,
      files,
      this.deps.fetch,
      (sha256) => `${this.deps.apiPrefix}/files/${sha256}`,
    )
    if (outcome.kind === 'failed') {
      this.retryLater()
      this.deps.report(MAP_UPDATE_EVENTS.downloadFailed, {
        sequence: manifest.sequence,
        reason: outcome.reason,
      })
      return
    }
    const failure =
      outcome.kind === 'rejected'
        ? outcome.check
        : await verifyBundle(manifest.format, outcome.files, SUPPORTED_MAP_FORMAT)

    record = this.load()
    const r = staged(record.state, remote, failure)
    const bundleFiles: BundleFiles = Object.fromEntries(
      wanted.map((f) => [f.name, { sha256: f.sha256, size: f.size }]),
    )
    const nextFiles =
      failure === '' ? { ...record.files, [remote.manifest]: bundleFiles } : record.files
    this.save({
      ...record,
      state: r.state,
      files: nextFiles,
      etag: fetched.etag,
      downloading: null,
      retryAttempts: 0,
    })
    if (failure === '') {
      this.deps.report(MAP_UPDATE_EVENTS.downloadCompleted, {
        sequence: manifest.sequence,
        version: manifest.version,
        bytes: outcome.kind === 'downloaded' ? outcome.fetchedBytes : 0,
        duration_ms: this.deps.now() - started,
      })
    } else {
      this.emit(r.events)
      await this.sweep(keptFiles(nextFiles, r.state))
    }
  }

  /**
   * A download did not finish: forget the ETag, so the next check fetches the
   * manifest again instead of a `304` that would leave the bundle half-done,
   * and run that check after the back-off rather than the full interval.
   */
  private retryLater(): void {
    const record = this.load()
    const attempts = (record.retryAttempts ?? 0) + 1
    this.save({
      ...record,
      etag: null,
      retryAttempts: attempts,
      checkedAt: this.deps.now() + retryDelayMs(attempts) - MANIFEST_CHECK_INTERVAL_MS,
    })
  }

  private async choose(mode: 'map' | 'peek'): Promise<MapBundleChoice> {
    if (this.deps.files === null) {
      // Nowhere to keep a download (no Cache Storage): the bundled map, no state.
      return { bundle: FALLBACK_BUNDLED, files: null, launched: false }
    }
    const bundled = await this.bundledBundle()
    this.bundled = bundled
    const loaded = this.deps.store.load()
    const files = loaded?.files ?? {}
    const state = await this.withCompleteness(
      loaded === null ? initialDeliveryState(bundled) : { ...loaded.state, bundled },
      files,
    )
    if (mode === 'peek') {
      const a = state.active
      const usable =
        a !== undefined &&
        a.source === 'downloaded' &&
        a.complete &&
        state.activeConfirmed &&
        a.format <= SUPPORTED_MAP_FORMAT &&
        !isBadManifest(state, a.manifest) &&
        a.sequence > bundled.sequence
      const bundle = usable ? a : bundled
      return {
        bundle,
        files: bundle.source === 'downloaded' ? (files[bundle.manifest] ?? null) : null,
        launched: false,
      }
    }
    const r = launch(state, SUPPORTED_MAP_FORMAT)
    this.save({
      state: r.state,
      files,
      checkedAt: loaded?.checkedAt ?? null,
      etag: loaded?.etag ?? null,
      downloading: loaded?.downloading ?? null,
      retryAttempts: loaded?.retryAttempts ?? 0,
    })
    this.emit(r.events)
    const bundle = r.chosen
    return {
      bundle,
      files: bundle.source === 'downloaded' ? (files[bundle.manifest] ?? null) : null,
      launched: true,
    }
  }

  /** Recomputes `complete` of every downloaded bundle from the file store. */
  private async withCompleteness(
    state: DeliveryState,
    files: DeliveryRecord['files'],
  ): Promise<DeliveryState> {
    const known = new Map<string, boolean>()
    const check = async (b: DeliveryBundle | undefined): Promise<DeliveryBundle | undefined> => {
      if (b === undefined || b.source === 'bundled') return b
      let complete = known.get(b.manifest)
      if (complete === undefined) {
        complete = await this.isComplete(files[b.manifest])
        known.set(b.manifest, complete)
      }
      return { ...b, complete }
    }
    const active = await check(state.active)
    const lastGood = await check(state.lastGood)
    const pending = await check(state.pending)
    const onDisk: DeliveryBundle[] = []
    for (const b of state.onDisk) onDisk.push((await check(b)) ?? b)
    return {
      ...state,
      ...(active === undefined ? {} : { active }),
      ...(lastGood === undefined ? {} : { lastGood }),
      ...(pending === undefined ? {} : { pending }),
      onDisk,
    }
  }

  private async isComplete(files: BundleFiles | undefined): Promise<boolean> {
    const store = this.deps.files
    if (files === undefined || store === null) return false
    try {
      for (const f of Object.values(files)) {
        if ((await store.get(f.sha256, f.size)) === null) return false
      }
      return true
    } catch {
      return false
    }
  }

  private async missingBytes(
    files: readonly ManifestFile[],
    store: BundleFileStore,
  ): Promise<number> {
    let total = 0
    for (const f of files) if ((await store.get(f.sha256, f.size)) === null) total += f.size
    return total
  }

  /** The bundled set's identity: sha256 of its `bundle.json` bytes, like the server's manifest `id`. */
  private async bundledBundle(): Promise<DeliveryBundle> {
    try {
      const bytes = await this.readBundledJson()
      if (bytes !== null) {
        const json = JSON.parse(new TextDecoder().decode(bytes)) as {
          sequence?: unknown
          format?: unknown
        }
        if (Number.isInteger(json.sequence) && Number.isInteger(json.format)) {
          return {
            source: 'bundled',
            sequence: json.sequence as number,
            format: json.format as number,
            manifest: await sha256Hex(bytes),
            complete: true,
          }
        }
      }
    } catch {
      // Unreadable bundle.json: still the bundled map, never newer than a download.
    }
    return FALLBACK_BUNDLED
  }

  /** The bundled set's `bundle.json` bytes, read once per page; `null` when unreadable. */
  private readBundledJson(): Promise<ArrayBuffer | null> {
    this.bundledJson ??= this.deps
      .fetch(this.bundledUrl('bundle.json'))
      .then((res) => (res.ok ? res.arrayBuffer() : null))
      .catch(() => null)
    return this.bundledJson
  }

  private async objectUrl(sha256: string, size: number, name: string): Promise<string> {
    const blob = await this.deps.files?.get(sha256, size).catch(() => null)
    if (blob === null || blob === undefined) {
      void this.reportBroken()
      return this.bundledUrl(name)
    }
    return this.deps.createObjectUrl(blob.slice(0, blob.size, mapFileContentType(name)))
  }

  private bundledUrl(name: string): string {
    return `${this.deps.bundledPath}/${name}`
  }

  /** The persisted record; a lost one restarts from the bundled map. Call after `choose`. */
  private load(): DeliveryRecord {
    const record = this.deps.store.load()
    if (record !== null) return record
    const bundled = this.bundled ?? FALLBACK_BUNDLED
    return {
      state: initialDeliveryState(bundled),
      files: {},
      checkedAt: null,
      etag: null,
    }
  }

  private save(record: DeliveryRecord): void {
    this.deps.store.save(record)
  }

  private async sweep(files: DeliveryRecord['files']): Promise<void> {
    const store = this.deps.files
    if (store === null) return
    const keep = new Set<string>()
    for (const bundle of Object.values(files))
      for (const f of Object.values(bundle)) keep.add(f.sha256)
    try {
      await store.sweep(keep)
    } catch {
      // A failed sweep leaves files behind; the next first frame retries.
    }
  }

  private emit(events: readonly DeliveryEvent[]): void {
    for (const e of events) this.deps.report(e.name, deliveryEventParams(e))
  }
}

/** The file lists of the downloaded bundles `state` still names. */
function keptFiles(files: DeliveryRecord['files'], state: DeliveryState): DeliveryRecord['files'] {
  const names = new Set(
    [state.active, state.lastGood, state.pending, ...state.onDisk]
      .filter((b): b is DeliveryBundle => b !== undefined && b.source === 'downloaded')
      .map((b) => b.manifest),
  )
  return Object.fromEntries(Object.entries(files).filter(([manifest]) => names.has(manifest)))
}
